import React, { useMemo } from "react";
import { Line } from "react-chartjs-2";
import {
  TimeSeriesPoint,
  extractTimeSeriesValues,
  getTimeSeriesPointLabel,
  getTimeSeriesPointValue,
} from "../../types/table";

interface SparklineProps {
  points: TimeSeriesPoint[];
  width?: number;
  height?: number;
  color?: string;
  fillColor?: string;
  valueFormatter?: (value: number) => string;
}

const Sparkline: React.FC<SparklineProps> = ({
  points,
  width = 120,
  height = 32,
  color = "#0f3557",
  fillColor = "rgba(15, 53, 87, 0.12)",
  valueFormatter,
}) => {
  const { labels, values } = useMemo(
    () => extractTimeSeriesValues(points),
    [points]
  );

  const data = useMemo(
    () => ({
      labels,
      datasets: [
        {
          data: values,
          borderColor: color,
          backgroundColor: fillColor,
          borderWidth: 1.5,
          fill: true,
          tension: 0.35,
          pointRadius: 0,
          pointHoverRadius: 3,
          pointHoverBackgroundColor: color,
          pointHoverBorderColor: "#ffffff",
          pointHoverBorderWidth: 1,
        },
      ],
    }),
    [labels, values, color, fillColor]
  );

  const options = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      animation: false as const,
      plugins: {
        legend: { display: false },
        title: { display: false },
        tooltip: {
          enabled: true,
          displayColors: false,
          callbacks: {
            title: (items: any[]) => (items[0] ? String(items[0].label) : ""),
            label: (item: any) => {
              const v = typeof item.parsed?.y === "number" ? item.parsed.y : item.parsed;
              if (typeof v !== "number") return String(v);
              return valueFormatter
                ? valueFormatter(v)
                : v.toLocaleString();
            },
          },
        },
      },
      scales: {
        x: { display: false },
        y: { display: false, beginAtZero: false },
      },
      interaction: { mode: "nearest" as const, intersect: false },
      elements: { line: { borderJoinStyle: "round" as const } },
    }),
    [valueFormatter]
  );

  if (!values.length) {
    return <span className="text-gray-400 text-xs">—</span>;
  }

  if (values.length === 1) {
    const v = values[0];
    const label = getTimeSeriesPointLabel(points[0]) || "";
    const formatted = valueFormatter ? valueFormatter(v) : v.toLocaleString();
    return (
      <span
        className="text-xs text-gray-700"
        title={`${label}: ${formatted}`}
      >
        {formatted}
      </span>
    );
  }

  const firstLabel = getTimeSeriesPointLabel(points[0] || {});
  const lastLabel = getTimeSeriesPointLabel(points[points.length - 1] || {});
  const lastValue = getTimeSeriesPointValue(points[points.length - 1] || {});
  const ariaLabel = `Series ${firstLabel} → ${lastLabel}, latest ${
    valueFormatter ? valueFormatter(lastValue) : lastValue.toLocaleString()
  }`;

  return (
    <div
      style={{ width, height }}
      role="img"
      aria-label={ariaLabel}
    >
      <Line data={data} options={options} />
    </div>
  );
};

export default Sparkline;
